import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Upload, FileText, Loader, CheckCircle, AlertCircle, Eye, Sparkles, Clock } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { formatFileSize } from '../lib/fileUtils';
import { formatRelativeTime } from '../lib/fileUtils';

export default function TaxDocuments() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [taxReturn, setTaxReturn] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('other');
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [returnRes, docsRes] = await Promise.all([
        api.get(`/api/tax-returns/${id}`),
        api.get(`/api/tax-returns/${id}/documents`)
      ]);
      
      setTaxReturn(returnRes.data);
      setDocuments(docsRes.data || []);
    } catch (error) {
      toast.error('Failed to load documents');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('document_category', selectedCategory);

      await api.post(`/api/tax-returns/${id}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Document uploaded successfully');
      setSelectedFile(null);
      setSelectedCategory('other');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleExtract = async (documentId) => {
    try {
      toast.info('Starting AI extraction...');
      await api.post(`/api/tax-returns/${id}/documents/${documentId}/extract`);
      toast.success('Extraction completed');
      loadData();
    } catch (error) {
      toast.error('Extraction failed');
      console.error(error);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      payg: 'bg-blue-100 text-blue-800',
      bank_statement: 'bg-green-100 text-green-800',
      dividend: 'bg-purple-100 text-purple-800',
      donation: 'bg-pink-100 text-pink-800',
      rental: 'bg-amber-100 text-amber-800',
      crypto: 'bg-red-100 text-red-800',
      other: 'bg-muted text-foreground'
    };
    return colors[category] || colors.other;
  };

  const getExtractionStatusBadge = (status) => {
    const config = {
      pending: { className: 'bg-muted text-foreground', icon: Clock, label: 'Pending' },
      processing: { className: 'bg-blue-100 text-blue-800', icon: Loader, label: 'Processing' },
      completed: { className: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Completed' },
      failed: { className: 'bg-red-100 text-red-800', icon: AlertCircle, label: 'Failed' }
    };
    
    const c = config[status] || config.pending;
    const Icon = c.icon;
    
    return (
      <Badge className={c.className}>
        <Icon className="w-3 h-3 mr-1" />
        {c.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="h-96 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}} data-testid="documents-title">
          Tax Documents
        </h1>
        <p className="text-muted-foreground mt-1">
          {taxReturn?.client_name} - Tax Year {taxReturn?.tax_year}
        </p>
      </div>

      {/* Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle>Upload Documents</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="category">Document Category *</Label>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger id="category" data-testid="doc-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="payg">PAYG Summary / Income Statement</SelectItem>
                  <SelectItem value="bank_statement">Bank Interest Statement</SelectItem>
                  <SelectItem value="dividend">Dividend Statement</SelectItem>
                  <SelectItem value="donation">Donation Receipt</SelectItem>
                  <SelectItem value="rental">Rental Property Statement</SelectItem>
                  <SelectItem value="crypto">Crypto Tax Summary</SelectItem>
                  <SelectItem value="other">Other Supporting Document</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="file">Select File *</Label>
              <Input
                id="file"
                type="file"
                onChange={handleFileSelect}
                accept=".pdf,.jpg,.jpeg,.png,.csv,.xlsx"
                data-testid="file-input"
              />
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              className="bg-primary hover:bg-primary/90"
              data-testid="upload-button"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? 'Uploading...' : 'Upload Document'}
            </Button>
            {selectedFile && (
              <span className="text-sm text-muted-foreground flex items-center">
                {selectedFile.name} ({formatFileSize(selectedFile.size)})
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Documents List */}
      <Card>
        <CardHeader>
          <CardTitle>Uploaded Documents ({documents.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {documents.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No Documents Uploaded Yet</h3>
              <p className="text-muted-foreground">Upload your tax documents to begin AI extraction</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="font-semibold">Filename</TableHead>
                    <TableHead className="font-semibold">Category</TableHead>
                    <TableHead className="font-semibold">Size</TableHead>
                    <TableHead className="font-semibold">Uploaded</TableHead>
                    <TableHead className="font-semibold">Extraction</TableHead>
                    <TableHead className="font-semibold">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {documents.map((doc) => (
                    <TableRow key={doc.id} className="hover:bg-muted/50" data-testid={`doc-row-${doc.id}`}>
                      <TableCell className="font-medium">{doc.filename}</TableCell>
                      <TableCell>
                        <Badge className={getCategoryColor(doc.document_category)}>
                          {doc.document_category.replace(/_/g, ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{formatFileSize(doc.file_size)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{formatRelativeTime(doc.uploaded_at)}</TableCell>
                      <TableCell>
                        {getExtractionStatusBadge(doc.extraction_status)}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          {doc.extraction_status === 'pending' && (
                            <Button
                              onClick={() => handleExtract(doc.id)}
                              size="sm"
                              variant="outline"
                              data-testid={`extract-${doc.id}`}
                            >
                              <Sparkles className="w-4 h-4 mr-1" />
                              Extract
                            </Button>
                          )}
                          {doc.extraction_status === 'completed' && (
                            <Button
                              onClick={() => navigate(`/tax-returns/${id}/documents/${doc.id}/extraction`)}
                              size="sm"
                              variant="outline"
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex justify-between">
        <Button
          onClick={() => navigate('/tax-returns')}
          variant="outline"
        >
          Back to Returns
        </Button>
      </div>
    </div>
  );
}

function Input({ type = 'text', ...props }) {
  return <input type={type} className="flex h-10 w-full rounded-md border border-border bg-card px-3 py-2 text-sm" {...props} />;
}